import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Calendar } from "lucide-react";

import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import type { SurveyOverviewHeaderProps } from "../types";

import "../styles.css";

const SurveyOverviewHeader = ({
  survey,
  organizationName,
  statistics = [],
  isStatisticsLoading = false,
}: SurveyOverviewHeaderProps) => {
  const { t } = useTranslation();

  const progressTotals = useMemo(() => {
    return statistics.reduce(
      (acc, section) => ({
        answeredRequired:
          acc.answeredRequired + section.answeredRequiredQuestionsCount,
        answeredOptional:
          acc.answeredOptional + section.answeredOptionalQuestionsCount,
        totalRequired: acc.totalRequired + section.requiredQuestionsCount,
        totalOptional: acc.totalOptional + section.optionalQuestionsCount,
      }),
      {
        answeredRequired: 0,
        answeredOptional: 0,
        totalRequired: 0,
        totalOptional: 0,
      },
    );
  }, [statistics]);

  const reportingPeriodEnd = survey.softDeadline || survey.hardDeadline;

  const requiredStrokeColor =
    progressTotals.totalRequired > 0 &&
    progressTotals.answeredRequired >= progressTotals.totalRequired
      ? "var(--green-500)"
      : "var(--red-500)";

  const optionalStrokeColor =
    progressTotals.totalOptional > 0 &&
    progressTotals.answeredOptional >= progressTotals.totalOptional
      ? "var(--green-500)"
      : "var(--orange-500)";

  return (
    <header className="survey-overview-header flex flex-wrap items-center gap-200 full-width">
      <BaseButton
        variant={BUTTON_VARIANTS.LINK}
        size={BUTTON_SIZES.EXTRA_SMALL}
        leadingIcon={<ArrowLeft size={20} />}
        onClick={(e) => {
          e.preventDefault();
          window.history.back();
        }}
      >
        {t("survey.labels.back")}
      </BaseButton>

      <div className="flex flex-wrap items-center justify-between gap-500 full-width">
        <div className="flex flex-col gap-100">
          <h1 className="header-m-medium">
            {organizationName
              ? `${organizationName} - ${survey.title}`
              : survey.title}
          </h1>

          <p className="survey-overview-header-reporting-period body-m-regular flex items-center gap-100">
            <Calendar size={20} />

            <span>
              {t("survey.labels.reportingPeriod")}:{" "}
              {DateTime.fromISO(survey.startDate).toFormat("dd MMMM yyyy")} -{" "}
              {DateTime.fromISO(reportingPeriodEnd).toFormat("dd MMMM yyyy")}
            </span>
          </p>
        </div>

        <div className="survey-overview-header-progress-bars flex gap-300 flex-shrink-0">
          {isStatisticsLoading ? (
            <BaseSpinner size="small" />
          ) : (
            <>
              <BaseProgressBar
                className="flex-1"
                value={progressTotals.answeredRequired}
                total={progressTotals.totalRequired || 1}
                label={t("survey.labels.required")}
                helpText={`${progressTotals.answeredRequired}/${progressTotals.totalRequired}`}
                strokeColor={requiredStrokeColor}
                strokeWidth={5}
              />
              <BaseProgressBar
                className="flex-1"
                value={progressTotals.answeredOptional}
                total={progressTotals.totalOptional || 1}
                label={t("survey.labels.optional")}
                helpText={`${progressTotals.answeredOptional}/${progressTotals.totalOptional}`}
                strokeColor={optionalStrokeColor}
                strokeWidth={5}
              />
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default SurveyOverviewHeader;
